import React, { useEffect, useRef, useState } from 'react';

import { Chip, Tooltip } from '@mui/material';
import { Refresh, CheckCircle } from '@mui/icons-material';

import { PerformanceStatusIndicatorProps } from './PerformanceStatusIndicator.types';
import './PerformanceStatusIndicator.css';

/**
 * PerformanceStatusIndicator Component
 *
 * Displays a fixed position indicator showing data update status.
 * Shows rotating refresh icon during updates and check icon when data is current.
 * Auto-refreshes every 30 seconds when data is updating.
 
 * <PerformanceStatusIndicator
 *   performanceStatus={status}
 *   onRefreshNeeded={() => refetchData()}
 * />
 */
export const PerformanceStatusIndicator: React.FC<PerformanceStatusIndicatorProps> = ({
  performanceStatus,
  onRefreshNeeded
}) => {
  const autoRefreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const onRefreshRef = useRef(onRefreshNeeded);
  onRefreshRef.current = onRefreshNeeded;

  useEffect(() => {
    if (performanceStatus?.isUpdating) {
      autoRefreshTimer.current = setInterval(() => {
        onRefreshRef.current?.();
      }, 15000);

      return () => {
        if (autoRefreshTimer.current) {
          clearInterval(autoRefreshTimer.current);
          autoRefreshTimer.current = null;
        }
      };
    }
  }, [performanceStatus?.isUpdating]);

  if (!performanceStatus) {
    return null;
  }

  const formatLastUpdated = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="performance-status-indicator">
      <Tooltip
        title={
          performanceStatus.lastUpdated
            ? `${performanceStatus.message}.\nОстаннє оновлення: ${formatLastUpdated(performanceStatus.lastUpdated)}`
            : performanceStatus.message
        }
        placement={isMobile ? 'bottom-end' : 'left'}
        open={tooltipOpen}
        onClose={() => setTooltipOpen(false)}
        disableHoverListener
        disableFocusListener
        disableTouchListener
        componentsProps={{
          tooltip: {
            style: { maxWidth: isMobile ? 'calc(100vw - 40px)' : undefined, fontSize: '0.75rem', whiteSpace: 'pre-line' }
          }
        }}
      >
        <Chip
          icon={performanceStatus.isUpdating ? <Refresh className="rotating-icon" /> : <CheckCircle />}
          label={isMobile ? undefined : (performanceStatus.isUpdating ? 'Оновлення даних...' : 'Дані актуальні')}
          color={performanceStatus.isUpdating ? 'warning' : 'success'}
          variant="filled"
          size="small"
          onClick={() => setTooltipOpen(v => !v)}
        />
      </Tooltip>
    </div>
  );
};
